import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connector, admin } from '../../services/api';
import { AdminUser } from '../../types/api';
import AgentConfigurator from '../agent-configurator/AgentConfigurator';
import type { AgentConfiguratorState } from '../../types/agentConfigurator';
import Notification from '../Notification';
import ConfirmModal from '../ConfirmModal';
import { useAuthContext } from '../../contexts/AuthContext';
import { capitalizeError } from '../../utils/errorUtils';
import {
  formatConnectorIntegrationCreatedAt,
  getConnectorIntegrationCreatedByDisplay,
} from '../../utils/connectorIntegrationDisplay';
import { NOTIFICATION_AUTO_HIDE_MS } from '../../constants/uiTiming';
import { mergePreservedConnectorConfig } from '../../utils/connectorConfigPreservation';
import { resolveStoredIntegrationId } from '../../utils/selectedIntegrationStorage';

function safeJsonParse(input: any): any | null {
  if (typeof input !== 'string') return null;
  try {
    return JSON.parse(input);
  } catch {
    return null;
  }
}

function extractConfigString(raw: any): { configString?: string; label?: string; dialog?: any[] } {
  const d = raw?.data ?? raw;

  // Common shapes:
  // - { config: { config: "{...}", dialog: [] }, label }
  // - { data: { config: { config: "{...}" }, label } }
  const configNode = d?.config ?? d?.data?.config ?? d?.data;
  const labelCandidates = [
    d?.label,
    d?.data?.label,
    d?.config?.label,
    d?.data?.config?.label,
    configNode?.label,
    configNode?.config?.label,
    configNode?.config?.config?.label,
  ];
  const label = labelCandidates.find((value) => typeof value === 'string') as string | undefined;
  const configString = configNode?.config?.config ?? configNode?.config ?? d?.config?.config;
  const dialog = configNode?.config?.dialog ?? configNode?.dialog ?? d?.config?.dialog ?? configNode?.config?.config?.dialog ?? [];

  return {
    configString: typeof configString === 'string' ? configString : undefined,
    label: typeof label === 'string' ? label : undefined,
    dialog: Array.isArray(dialog) ? dialog : [],
  };
}

export interface ConnectorPanelProps {
  selectedIntegrationId: string | null;
  onSelectIntegrationId: (id: string | null) => void;
  currentUserId?: string;
  /** Opens the admin page guided tour (Help next to section title). */
  onOpenAdminTour?: () => void;
}

const ConnectorPanel: React.FC<ConnectorPanelProps> = ({
  selectedIntegrationId,
  onSelectIntegrationId,
  currentUserId,
  onOpenAdminTour,
}) => {
  const { user, isLoading: authLoading } = useAuthContext();
  
  // Все хуки должны быть вызваны до любых условных возвратов
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [integrations, setIntegrations] = useState<any[]>([]);
  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [dialogEnvelope, setDialogEnvelope] = useState<any[]>([]);
  const [configLabel, setConfigLabel] = useState<string | undefined>(undefined);
  const [configValue, setConfigValue] = useState<AgentConfiguratorState | null>(null);
  /** Full last loaded config JSON — merged on Save so fields absent from the UI are kept. */
  const connectorConfigPreservationRef = useRef<Record<string, unknown> | null>(null);

  // User assignment modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersSearchQuery, setUsersSearchQuery] = useState('');
  const [assigningUserId, setAssigningUserId] = useState<string | null>(null);

  // Notification state
  const [notification, setNotification] = useState<{
    isVisible: boolean;
    type: 'success' | 'error' | 'info';
    message: string;
  }>({
    isVisible: false,
    type: 'success',
    message: '',
  });

  // Delete confirmation modal state
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{
    isOpen: boolean;
    integrationId: string | null;
  }>({
    isOpen: false,
    integrationId: null,
  });

  // Edit integration modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState<{ url: string; apiKey: string }>({
    url: '',
    apiKey: '',
  });
  const [editFormLoading, setEditFormLoading] = useState(false);
  const [adminIntegrationCreatedByExpandedId, setAdminIntegrationCreatedByExpandedId] = useState<string | null>(null);

  const selectedIntegration = useMemo(() => {
    if (!selectedIntegrationId) return null;
    return integrations.find((i) => (i?.id || i?._id) === selectedIntegrationId) || null;
  }, [integrations, selectedIntegrationId]);
  
  // Проверка роли - только админы могут использовать админские функции
  const userRole = (user as any)?.role;
  const isAdmin = typeof userRole === 'string' && userRole.toLowerCase() === 'admin';

  const adminIntegrationRestoreRef = useRef(false);

  const loadIntegrations = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await connector.listIntegrationsAdmin();
      const items = resp?.data ?? resp?.items ?? resp ?? [];
      const list = Array.isArray(items) ? items : [];
      setIntegrations(list);

      if (!adminIntegrationRestoreRef.current && !selectedIntegrationId && list.length > 0) {
        adminIntegrationRestoreRef.current = true;
        const storedId = resolveStoredIntegrationId('admin', (user as { id?: string })?.id, list);
        if (storedId) {
          onSelectIntegrationId(storedId);
        }
      }
    } catch (e: any) {
      setError(capitalizeError(e?.message) || 'Failed to load connector integrations');
      setIntegrations([]);
    } finally {
      setLoading(false);
    }
  };

  const loadConfig = async (integrationId: string) => {
    connectorConfigPreservationRef.current = null;
    setConfigLoading(true);
    setConfigError(null);
    try {
      const resp = await connector.getIntegrationConfig(integrationId);
      const { configString, label, dialog } = extractConfigString(resp);
      setConfigLabel(label);
      setDialogEnvelope(dialog || []);
      const parsed = safeJsonParse(configString);
      if (parsed && typeof parsed === 'object' && parsed.coordinator && Array.isArray(parsed.workers)) {
        connectorConfigPreservationRef.current = parsed as Record<string, unknown>;
        setConfigValue(parsed as AgentConfiguratorState);
      } else {
        // If config is missing or not in expected shape, start from empty
        setConfigValue(null);
      }
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 403) {
        setConfigError(
          '403 Forbidden: your user is not assigned to this integration. Click "Assign me" (admin) and retry.'
        );
      } else {
        setConfigError(capitalizeError(e?.message) || 'Failed to load integration config');
      }
      connectorConfigPreservationRef.current = null;
      setConfigValue(null);
    } finally {
      setConfigLoading(false);
    }
  };

  useEffect(() => {
    void loadIntegrations();
  }, []);

  useEffect(() => {
    if (!selectedIntegrationId) return;
    void loadConfig(selectedIntegrationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIntegrationId]);

  // Block body scroll when modal is open
  useEffect(() => {
    if (showAssignModal) {
      // Save current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      // Restore scroll position
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
    return () => {
      // Cleanup on unmount
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
    };
  }, [showAssignModal]);

  const loadUsers = async (searchQuery: string = '') => {
    setUsersLoading(true);
    try {
      const response = await admin.getUsers({
        limit: 100,
        offset: 0,
        q: searchQuery,
      });
      const items = response?.success && response?.data ? response.data.items : [];
      setUsers(Array.isArray(items) ? items : []);
    } catch (e: any) {
      console.error('Failed to load users:', e);
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    if (showAssignModal) {
      void loadUsers(usersSearchQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAssignModal, usersSearchQuery]);

  // Если не админ, не показываем админские функции (после ВСЕХ хуков)
  if (authLoading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div className="spinner spinner--small"></div>
        <p>Loading...</p>
      </div>
    );
  }
  
  if (!isAdmin) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>You do not have permission to access admin functions.</p>
      </div>
    );
  }

  const handleSaveConfig = async (cfg: AgentConfiguratorState, label?: string) => {
    if (!selectedIntegrationId) throw new Error('No integration selected');
    const merged = mergePreservedConnectorConfig(connectorConfigPreservationRef.current, cfg);
    connectorConfigPreservationRef.current = merged;
    const body = {
      config: {
        config: JSON.stringify(merged),
        dialog: dialogEnvelope || [],
        label: label || undefined,
      },
      label: label || undefined,
    };
    await connector.setIntegrationConfig(selectedIntegrationId, body);
    // Обновляем configLabel после успешного сохранения
    if (label !== undefined) {
      setConfigLabel(label || undefined);
    }
  };

  const handleAssignUser = async (userId: string) => {
    if (!selectedIntegrationId) return;
    setAssigningUserId(userId);
    try {
      setError(null);
      await connector.assignUserToIntegration(selectedIntegrationId, userId);
      setShowAssignModal(false);
      setUsersSearchQuery('');
      // After assigning, re-load config
      await loadConfig(selectedIntegrationId);
    } catch (e: any) {
      setError(capitalizeError(e?.message) || 'Failed to assign user to integration');
    } finally {
      setAssigningUserId(null);
    }
  };

  const handleCopyId = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      setNotification({
        isVisible: true,
        type: 'success',
        message: 'Integration ID copied to clipboard!',
      });
      setTimeout(() => {
        setNotification(prev => ({ ...prev, isVisible: false }));
      }, NOTIFICATION_AUTO_HIDE_MS);
    } catch (e: any) {
      setNotification({
        isVisible: true,
        type: 'error',
        message: 'Failed to copy ID',
      });
      setTimeout(() => {
        setNotification(prev => ({ ...prev, isVisible: false }));
      }, NOTIFICATION_AUTO_HIDE_MS);
    }
  };

  const handleDeleteIntegration = (integrationId: string) => {
    setDeleteConfirmModal({
      isOpen: true,
      integrationId,
    });
  };

  const confirmDeleteIntegration = async () => {
    const integrationId = deleteConfirmModal.integrationId;
    if (!integrationId) return;

    setDeleteConfirmModal({ isOpen: false, integrationId: null });
    setLoading(true);
    setError(null);
    try {
      // Use admin delete method if available, otherwise use regular delete
      await connector.deleteIntegrationAdmin(integrationId);
      setNotification({
        isVisible: true,
        type: 'success',
        message: 'Integration deleted successfully!',
      });
      setTimeout(() => {
        setNotification(prev => ({ ...prev, isVisible: false }));
      }, NOTIFICATION_AUTO_HIDE_MS);
      
      // If deleted integration was selected, clear selection
      if (selectedIntegrationId === integrationId) {
        onSelectIntegrationId(null);
        setConfigValue(null);
        setConfigLabel(undefined);
        setDialogEnvelope([]);
      }
      
      // Reload integrations list
      await loadIntegrations();
    } catch (e: any) {
      const errorMsg = capitalizeError(e?.message) || 'Failed to delete integration';
      setError(errorMsg);
      setNotification({
        isVisible: true,
        type: 'error',
        message: errorMsg,
      });
      setTimeout(() => {
        setNotification(prev => ({ ...prev, isVisible: false }));
      }, NOTIFICATION_AUTO_HIDE_MS);
    } finally {
      setLoading(false);
    }
  };

  const handleEditIntegration = async (integrationId: string) => {
    if (!selectedIntegrationId || selectedIntegrationId !== integrationId) {
      setError('Please select this integration first to edit it');
      return;
    }
    const integration = integrations.find((i) => (i?.id || i?._id) === integrationId);
    if (integration) {
      // Загружаем полную информацию об интеграции, чтобы получить API Key
      try {
        setEditFormLoading(true);
        const fullIntegration = await connector.getIntegration(integrationId);
        const integrationData = fullIntegration?.data || fullIntegration;
        // Бэкенд возвращает apikey в нижнем регистре, проверяем оба варианта
        const apiKeyValue = integrationData?.apiKey || integrationData?.apikey || integration?.apiKey || integration?.apikey || '';
        setEditFormData({
          url: integrationData?.url || integration?.url || integration?.endpoint || '',
          apiKey: apiKeyValue,
        });
        setShowEditModal(true);
      } catch (e: any) {
        // Если не удалось загрузить полную информацию, используем данные из списка
        console.warn('Failed to load full integration data, using list data:', e);
        // Проверяем оба варианта названия поля
        const apiKeyValue = integration?.apiKey || integration?.apikey || '';
        setEditFormData({
          url: integration?.url || integration?.endpoint || '',
          apiKey: apiKeyValue,
        });
        setShowEditModal(true);
      } finally {
        setEditFormLoading(false);
      }
    }
  };

  const handleSaveEditIntegration = async () => {
    if (!selectedIntegrationId) return;
    if (!editFormData.url.trim()) {
      setError('URL is required');
      // Auto-clear validation error after 5 seconds
      setTimeout(() => setError(null), NOTIFICATION_AUTO_HIDE_MS);
      return;
    }
    setEditFormLoading(true);
    setError(null);
    try {
      await connector.patchIntegrationAdmin(selectedIntegrationId, {
        url: editFormData.url,
        apiKey: editFormData.apiKey || undefined,
      });
      setShowEditModal(false);
      setEditFormData({ url: '', apiKey: '' });
      setNotification({
        isVisible: true,
        type: 'success',
        message: 'Integration updated successfully!',
      });
      setTimeout(() => {
        setNotification(prev => ({ ...prev, isVisible: false }));
      }, NOTIFICATION_AUTO_HIDE_MS);
      await loadIntegrations();
    } catch (e: any) {
      const errorMessage = capitalizeError(e?.message) || 'Failed to update integration';
      setError(errorMessage);
      // Auto-clear error after 7 seconds
      setTimeout(() => setError(null), NOTIFICATION_AUTO_HIDE_MS);
    } finally {
      setEditFormLoading(false);
    }
  };

  return (
    <div className="admin-section" id="admin-connector-root">
      <div className="admin-section-header" id="admin-connector-section-header">
        <h2>Connector Integrations</h2>
        <div className="header-actions">
          {onOpenAdminTour && (
            <button
              type="button"
              className="action-btn"
              onClick={onOpenAdminTour}
              style={{ background: '#0099FA', color: '#FFFFFF' }}
              title="Guided tour for this section"
            >
              <i className="fas fa-question-circle" aria-hidden="true" />
              <span className="btn-text">Help</span>
            </button>
          )}
          <button onClick={() => void loadIntegrations()} className="action-btn refresh-btn" disabled={loading}>
            <i className="fas fa-sync-alt" aria-hidden="true"></i>
            <span className="btn-text">{loading ? 'Loading…' : 'Refresh'}</span>
          </button>
          <button
            onClick={() => {
              if (!selectedIntegrationId) {
                setError('Please select an integration first');
                return;
              }
              setShowAssignModal(true);
              setUsersSearchQuery('');
            }}
            className="action-btn apply-btn"
            disabled={loading || !selectedIntegrationId}
            title={!selectedIntegrationId ? 'Select an integration first' : 'Assign user to selected integration'}
          >
            <i className="fas fa-user-plus" aria-hidden="true"></i>
            <span className="btn-text">Assign User</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="admin-message error" style={{ margin: 0, marginBottom: 16 }}>
          <i className="fas fa-exclamation-circle"></i> {error}
        </div>
      )}

      {/* Assign User Modal */}
      {showAssignModal && (
        <div className="modal-overlay">
          <div className="modal-content modal-content--wide">
            <div className="modal-header">
              <h3>Assign User to Integration</h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowAssignModal(false);
                  setUsersSearchQuery('');
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label>
                  Search Users
                  <input
                    type="text"
                    value={usersSearchQuery}
                    onChange={(e) => setUsersSearchQuery(e.target.value)}
                    placeholder="Search by email or name..."
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #C6E0F7',
                      borderRadius: '6px',
                      fontSize: '14px',
                    }}
                  />
                </label>
              </div>
               {usersLoading ? (
                 <div style={{ textAlign: 'center', padding: '20px' }}>
                   <div className="spinner spinner--small"></div>
                   <p>Loading users...</p>
                 </div>
               ) : users.length === 0 ? (
                 <div style={{ textAlign: 'center', padding: '20px', color: '#5B7893' }}>
                   No users found
                 </div>
               ) : (
                 <div style={{ maxHeight: '500px', overflowY: 'auto', overflowX: 'hidden' }}>
                   <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                     {users.map((user) => {
                       const userId = String(user.id || '');
                       const isAssigning = assigningUserId === userId;
                       return (
                         <div
                           key={userId}
                           style={{
                             display: 'flex',
                             alignItems: 'center',
                             justifyContent: 'space-between',
                             padding: '12px 16px',
                             background: '#FFFFFF',
                             borderRadius: '8px',
                             border: '1px solid #E3F0FB',
                             gap: '12px',
                             flexWrap: 'wrap',
                           }}
                         >
                           <div style={{ flex: '1', minWidth: '200px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                             <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                               <span style={{ fontWeight: 600, color: '#0A2A47', fontSize: '14px' }}>
                                 {user.email || '-'}
                               </span>
                               <span
                                 style={{
                                   padding: '2px 8px',
                                   borderRadius: '4px',
                                   fontSize: '11px',
                                   fontWeight: 600,
                                   background: user.role === 'admin' ? '#E3F0FB' : '#F5F9FE',
                                   color: user.role === 'admin' ? '#0099FA' : '#5B7893',
                                   whiteSpace: 'nowrap',
                                 }}
                               >
                                 {user.role || 'user'}
                               </span>
                             </div>
                             {user.name && (
                               <span style={{ fontSize: '13px', color: '#5B7893' }}>
                                 {user.name}
                               </span>
                             )}
                           </div>
                           <button
                             className="edit-btn"
                             onClick={() => handleAssignUser(userId)}
                             disabled={isAssigning || !selectedIntegrationId}
                             style={{ 
                               minWidth: '100px', 
                               opacity: isAssigning ? 0.6 : 1,
                               flexShrink: 0,
                             }}
                           >
                             {isAssigning ? (
                               <>
                                 <div className="spinner spinner--small" style={{ width: '12px', height: '12px', borderWidth: '2px', display: 'inline-block' }}></div>
                                 <span style={{ marginLeft: '6px' }}>Assigning...</span>
                               </>
                             ) : (
                               <>
                                 <i className="fas fa-user-plus"></i> Assign
                               </>
                             )}
                           </button>
                         </div>
                       );
                     })}
                   </div>
                 </div>
               )}
            </div>
          </div>
        </div>
      )}

      <div className="admin-table-wrapper" id="admin-connector-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>URL</th>
              <th>Created by</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {integrations.length === 0 ? (
              <tr>
                <td colSpan={4} className="empty-cell">
                  <div className="empty-state">
                    <div className="empty-title">No integrations</div>
                    <div className="empty-subtitle">Create an integration in backend or refresh.</div>
                  </div>
                </td>
              </tr>
            ) : (
              integrations.map((it) => {
                const id = String(it?.id || it?._id || '');
                const isSelected = selectedIntegrationId === id;
                const createdBy = getConnectorIntegrationCreatedByDisplay(it);
                const createdByExpandable = createdBy.full !== createdBy.text && createdBy.full !== '-';
                const isCreatedByExpanded = adminIntegrationCreatedByExpandedId === id;
                return (
                  <tr key={id}>
                    <td data-label="URL">{it?.url || it?.endpoint || '-'}</td>
                    <td data-label="Created by">
                      <div
                        title={
                          createdByExpandable
                            ? isCreatedByExpanded
                              ? 'Click to show shortened'
                              : 'Click to show full value'
                            : createdBy.title || undefined
                        }
                        role={createdByExpandable ? 'button' : undefined}
                        tabIndex={createdByExpandable ? 0 : undefined}
                        onClick={() =>
                          createdByExpandable &&
                          setAdminIntegrationCreatedByExpandedId((cur) => (cur === id ? null : id))
                        }
                        onKeyDown={(e) => {
                          if (!createdByExpandable) return;
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setAdminIntegrationCreatedByExpandedId((cur) => (cur === id ? null : id));
                          }
                        }}
                        style={{
                          cursor: createdByExpandable ? 'pointer' : 'default',
                          wordBreak: isCreatedByExpanded ? 'break-all' : 'normal',
                          userSelect: isCreatedByExpanded ? 'text' : undefined,
                        }}
                      >
                        {isCreatedByExpanded ? createdBy.full : createdBy.text}
                      </div>
                    </td>
                    <td data-label="Created">{formatConnectorIntegrationCreatedAt(it?.created_at)}</td>
                    <td data-label="Actions" className="actions-cell">
                      <div className="actions-wrap">
                        <button
                          className="edit-btn"
                          onClick={() => onSelectIntegrationId(isSelected ? null : id)}
                          title="Use this integration_id for Virtual User Service"
                        >
                          <i className="fas fa-bullseye"></i> {isSelected ? 'Selected' : 'Select'}
                        </button>
                        <button
                          className="edit-btn"
                          onClick={() => handleEditIntegration(id)}
                          disabled={!isSelected}
                          title={isSelected ? 'Edit integration URL and API key' : 'Select this integration first to edit it'}
                          style={!isSelected ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                        >
                          <i className="fas fa-edit"></i> Edit
                        </button>
                        <button
                          className="edit-btn"
                          onClick={() => handleCopyId(id)}
                          title={`Copy integration ID: ${id}`}
                        >
                          <i className="fas fa-copy"></i> Copy ID
                        </button>
                        <button
                          className="edit-btn delete-btn"
                          onClick={() => handleDeleteIntegration(id)}
                          title="Delete integration"
                          style={{ color: '#FFFFFF' }}
                        >
                          <i className="fas fa-trash"></i> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div id="admin-connector-configurator" style={{ marginTop: 24 }}>
        <h3 style={{ marginBottom: 12, fontSize: 18, fontWeight: 800, color: '#0A2A47' }}>
          Integration Configurator {selectedIntegrationId ? `(${selectedIntegrationId})` : ''}
        </h3>

        {!selectedIntegrationId ? (
          <div className="admin-message error" style={{ margin: 0 }}>
            <i className="fas fa-exclamation-circle"></i> Select an integration to edit its config.
          </div>
        ) : configLoading ? (
          <div className="admin-loading" style={{ padding: 24 }}>
            <div className="spinner spinner--small"></div>
            <p>Loading config…</p>
          </div>
        ) : (
          <>
            {configError && (
              <div className="admin-message error" style={{ margin: 0, marginBottom: 16 }}>
                <i className="fas fa-exclamation-circle"></i> {configError}
              </div>
            )}

            <AgentConfigurator
              initialConfig={configValue}
              initialLabel={configLabel}
              onSave={handleSaveConfig}
              onCancel={undefined}
              onImportRawConfig={(raw) => {
                connectorConfigPreservationRef.current = raw;
              }}
            />
          </>
        )}
      </div>

      {/* Notification */}
      {notification.isVisible && (
        <Notification
          type={notification.type}
          message={notification.message}
          isVisible={notification.isVisible}
          onClose={() => setNotification(prev => ({ ...prev, isVisible: false }))}
          duration={NOTIFICATION_AUTO_HIDE_MS}
          position="top-right"
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirmModal.isOpen}
        title="Delete Integration"
        message="Are you sure you want to delete this integration? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isConfirmLoading={loading}
        onConfirm={confirmDeleteIntegration}
        onCancel={() => setDeleteConfirmModal({ isOpen: false, integrationId: null })}
      />

      {/* Edit Integration Modal */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Edit Integration</h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowEditModal(false);
                  setEditFormData({ url: '', apiKey: '' });
                  setError(null);
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label>
                  URL <span style={{ color: '#FF8300' }}>*</span>
                  <input
                    type="text"
                    value={editFormData.url}
                    onChange={(e) => {
                      setEditFormData({ ...editFormData, url: e.target.value });
                      setError(null); // Clear error when user starts typing
                    }}
                    placeholder="https://dev.sdk.aipsy.tech"
                    disabled={editFormLoading}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #C6E0F7',
                      borderRadius: '6px',
                      fontSize: '14px',
                    }}
                  />
                </label>
              </div>
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label>
                  API Key
                  <input
                    type="text"
                    value={editFormData.apiKey}
                    onChange={(e) => {
                      setEditFormData({ ...editFormData, apiKey: e.target.value });
                      setError(null); // Clear error when user starts typing
                    }}
                    placeholder="Enter API key if required"
                    disabled={editFormLoading}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #C6E0F7',
                      borderRadius: '6px',
                      fontSize: '14px',
                    }}
                  />
                </label>
                <div style={{ marginTop: '6px', fontSize: '12px', color: '#5B7893' }}>
                  Used for authentication if your integration requires it
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={() => {
                  setShowEditModal(false);
                  setEditFormData({ url: '', apiKey: '' });
                  setError(null);
                }}
                disabled={editFormLoading}
              >
                Cancel
              </button>
              <button
                className="btn-save"
                onClick={handleSaveEditIntegration}
                disabled={editFormLoading || !editFormData.url.trim()}
              >
                {editFormLoading ? 'Updating…' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectorPanel;
